<template lang="pug">
.monitor-viewer-cams
  .cams-header
    .header-left
      ui-button(@click='goBack', icon='el-icon-arrow-left') 返回项目列表
      h1 {{ projectInfo.title }} - 监控列表
    .header-right
      .search-box
        ui-input(
          v-model='searchKeyword',
          placeholder='搜索监控设备',
          prefix-icon='el-icon-search',
          clearable,
          @input='handleSearch'
        )
      .stats
        .stat-item
          span.label 在线:
          span.value.online {{ onlineCount }}
        .stat-item
          span.label 离线:
          span.value.offline {{ offlineCount }}
        .stat-item
          span.label 总计:
          span.value.total {{ cams.length }}

  .cams-container
    .cams-grid(v-if='filteredCams.length')
      .cam-card(v-for='cam in filteredCams', :key='cam.id', @click='viewCam(cam)')
        .cam-preview
          .cam-placeholder
            i.el-icon-video-camera
            span {{ cam.state ? '在线' : '离线' }}
        .cam-info
          .cam-title {{ cam.title }}
          .cam-status(:class='{ online: cam.state, offline: !cam.state }')
            span {{ cam.state ? '在线' : '离线' }}
        .cam-actions
          ui-button(type='primary', size='small') 查看监控
    .empty-state(v-else)
      template(v-if='searchKeyword')
        p 未找到匹配"{{ searchKeyword }}"的监控设备
        p 请尝试其他关键词或清空搜索
      template(v-else)
        p 该项目暂无监控设备
        p 请联系管理员添加监控设备
</template>

<script>
import { _ } from '@yishitec/web';

export default {
  name: 'MonitorViewerProjectCams',
  data() {
    return {
      projectId: null,
      projectInfo: {},
      cams: [],
      loading: false,
      searchKeyword: '',
    };
  },
  computed: {
    filteredCams() {
      let filtered = this.cams;

      // 搜索过滤
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase();
        filtered = filtered.filter((cam) => cam.title.toLowerCase().includes(keyword));
      }

      // 排序：先在线再离线
      return filtered.sort((a, b) => {
        if (a.state === b.state) return 0;
        return a.state ? -1 : 1; // 在线(true)排在前面
      });
    },
    onlineCount() {
      return this.cams.filter((cam) => cam.state).length;
    },
    offlineCount() {
      return this.cams.filter((cam) => !cam.state).length;
    },
  },
  async mounted() {
    this.projectId = this.$route.params.projectId;
    await this.loadProjectInfo();
    await this.loadCams();
  },
  methods: {
    async loadProjectInfo() {
      try {
        const projects = await this.api('/api/monitor-viewer/projects')();
        this.projectInfo = projects.find((p) => p.id == this.projectId) || {};
      } catch (e) {
        this.$message.error('获取项目信息失败');
      }
    },

    async loadCams() {
      try {
        this.loading = true;
        this.cams = await this.api(`/api/monitor-viewer/cams/${this.projectId}`)();
      } catch (e) {
        this.$message.error('获取监控列表失败');
        this.cams = [];
      } finally {
        this.loading = false;
      }
    },

    viewCam(cam) {
      this.$router.push(`/monitor-viewer/cam/${cam.id}/view`);
    },

    goBack() {
      this.$router.push('/monitor-viewer/');
    },

    handleSearch() {
      // 搜索功能通过 computed 属性 filteredCams 自动处理
    },
  },
};
</script>

<style lang="less" scoped>
.monitor-viewer-cams {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
}

.cams-header {
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header-left {
    display: flex;
    align-items: center;
    gap: 15px;

    h1 {
      margin: 0;
      color: #333;
      font-size: 18px;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 20px;

    .search-box {
      width: 200px;
    }

    .stats {
      display: flex;
      gap: 15px;

      .stat-item {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 14px;

        .label {
          color: #666;
        }

        .value {
          font-weight: 600;

          &.online {
            color: #67c23a;
          }

          &.offline {
            color: #f56c6c;
          }

          &.total {
            color: #333;
          }
        }
      }
    }
  }
}

.cams-container {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 15px;
}

.cam-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;

  &:hover {
    border-color: #409eff;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
    transform: translateY(-2px);
  }
}

.cam-preview {
  height: 120px;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .cam-placeholder {
    text-align: center;
    color: #999;

    i {
      font-size: 32px;
      display: block;
      margin-bottom: 8px;
    }

    span {
      font-size: 12px;
    }
  }
}

.cam-info {
  padding: 12px;

  .cam-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .cam-status {
    font-size: 12px;

    &.online {
      color: #67c23a;
    }

    &.offline {
      color: #f56c6c;
    }
  }
}

.cam-actions {
  padding: 0 12px 12px;
  text-align: right;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;

  p {
    margin: 10px 0;
    font-size: 14px;
  }
}
</style>
